import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { type ChatInteractionMode, isBrandInsightsContext } from '@trinity/chat';
import { AuraChatContext } from '@trinity/chat-components/components/BrandInsights/context.js';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat.js';
import {
  ActiveChatSessionContext,
  ChatContextItemsContext,
  ChatSettingsToggleContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { KolWelcome } from '@trinity/chat-components/components/Chat/Kol.Welcome.js';
import {
  GetStartedContext,
  ChatGetStarted,
} from '@trinity/chat-components/components/Chat.GetStarted/index.js';
import { ChatHeader } from '@trinity/chat-components/components/Chat.Header/index.js';
import { ChatSettings } from '@trinity/chat-components/components/Chat.Screen/Chat.Setting.js';
import { ChatSidebar } from '@trinity/chat-components/components/Chat.Screen/Sidebar.js';
import {
  AvatarPanelContext,
  useAvatarPanel,
} from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context.js';
import { HeyGenPanel } from '@trinity/chat-components/components/DigitalTwin/HeyGenPanel.js';
import { HeyGenResponseBridge } from '@trinity/chat-components/components/LiveAvatar/HeyGenResponseBridge.js';
import { LiveAvatar } from '@trinity/chat-components/components/LiveAvatar/LiveAvatar.js';
import { LiveAvatarSessionManager } from '@trinity/chat-components/components/LiveAvatar/LiveAvatarSessionManager.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context.js';
import {
  useChatInteractionModes,
  useChatLiveAvatarOptions,
} from '@trinity/chat-components/containers/Chat/selectors.js';
import { useMonitorChatIdentifier } from '@trinity/chat-components/containers/Chat/useMonitorHashChatIdentifier.js';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import { ChatScreenContainer } from './Container';

const BrandInsightsChatScreen = () => {
  const [videoExpanded, setVideoExpanded] = useState(false);

  const settingsInteractionModes = useChatInteractionModes();

  const [interactionMode, setInteractionMode] = useState<ChatInteractionMode>(
    settingsInteractionModes.default
  );

  useMonitorChatIdentifier();

  const isLoading = useIsChatLoading();

  if (isLoading) {
    return null;
  }

  return (
    <Root>
      <MainChat>
        <AvatarPanelContext.Provider
          value={{
            expanded: videoExpanded,
            setExpanded: setVideoExpanded,
            isDigitalTwin: interactionMode === 'video',
            isNewChatInteractionLayout: true,
            availableModes: settingsInteractionModes.available,
            defaultInteractionMode: settingsInteractionModes.default,
            interactionMode,
            setInteractionMode,
            segmentIconUrl: undefined,
          }}
        >
          {interactionMode === 'video' && <ChatVideoInteraction />}
          {interactionMode !== 'video' && <ChatTextOrVoiceInteraction />}
        </AvatarPanelContext.Provider>
      </MainChat>
    </Root>
  );
};

const ChatVideoInteraction = () => {
  const [settings] = useContext(ChatSettingsToggleContext);
  const { theme } = useContext(AppThemingContext);
  const [sessionKey, setSessionKey] = useState(0);
  const { setExpanded, expanded } = useAvatarPanel();
  const settingsAvatarOptions = useChatLiveAvatarOptions();

  const style = theme?.enabled
    ? {
        backgroundImage: 'none',
        backgroundColor: '#fafafa',
      }
    : {};

  const showSettings = !!settings;
  const showChat = !showSettings;

  const avatarId = settingsAvatarOptions?.avatarId;
  const ttsConfig = settingsAvatarOptions?.ttsConfig;
  const sessionActive = !!avatarId;

  const handleToggleExpand = () => setExpanded(!expanded);
  const handleReinitialize = useCallback(() => setSessionKey((k) => k + 1), []);

  return (
    <>
      <ChatSidebar />
      <Wrap style={style}>
        {!showChat ? (
          <ChatSettings />
        ) : (
          <VideoWrap expanded={expanded}>
            <LiveAvatarSessionManager key={sessionKey} avatarId={avatarId} ttsConfig={ttsConfig}>
              <HeyGenPanel
                expanded={expanded}
                onToggleExpand={handleToggleExpand}
                sessionActive={sessionActive}
                onReinitialize={handleReinitialize}
              >
                <LiveAvatar />
              </HeyGenPanel>
              <HeyGenResponseBridge />
            </LiveAvatarSessionManager>
            <ChatInteractionWrapper>
              <ChatHeader />
              <ChatInstance />
            </ChatInteractionWrapper>
          </VideoWrap>
        )}
      </Wrap>
    </>
  );
};

const ChatTextOrVoiceInteraction = () => {
  const [getStarted, dismissGetStarted] = useContext(GetStartedContext);
  const { dismiss, enabled, visible } = useContext(ChatWelcomeScreenContext);
  const { auraChatStatus } = useContext(AuraChatContext);
  const [settings] = useContext(ChatSettingsToggleContext);
  const { theme } = useContext(AppThemingContext);
  const { context } = useContext(ActiveChatSessionContext);

  const brandInsights = isBrandInsightsContext(context);

  const style = theme?.enabled
    ? {
        backgroundImage: 'none',
        backgroundColor: '#fafafa',
      }
    : {};

  const showSettings = !!settings;
  const showWelcomeScreen = !showSettings && enabled && visible && !brandInsights;
  const showGetStarted = !showSettings && getStarted && !enabled;
  const showAuraChat = !showSettings && !showWelcomeScreen && !showGetStarted && auraChatStatus;
  const showChat = !showSettings && !showWelcomeScreen && !showGetStarted;

  return (
    <>
      <ChatSidebar />

      <Wrap style={style}>
        {!showSettings && !showAuraChat && <ChatHeader />}

        {showSettings && <ChatSettings />}
        {showWelcomeScreen && <KolWelcome onClick={dismiss} />}
        {showGetStarted && <StyledGetStarted onClick={dismissGetStarted} />}
        {showAuraChat && (
          <iframe
            src="https://realinsightsauras.trinitylifesciences.com/home/hcp_auras"
            width="100%"
            height="100%"
            allow="microphone"
            frameBorder="0"
          ></iframe>
        )}
        {showChat && <ChatInstance />}
      </Wrap>
    </>
  );
};

function useIsChatLoading() {
  const contextItems = useContext(ChatContextItemsContext);
  const chat = useContext(ActiveChatSessionContext);

  return useMemo(() => {
    return !contextItems || contextItems.length === 0 || !chat.context;
  }, [contextItems, chat.context]);
}

export const ChatScreen = () => {
  return (
    <ChatScreenContainer>
      <BrandInsightsChatScreen />
    </ChatScreenContainer>
  );
};

const Root = styled.div({
  height: `calc(100vh - var(--navbar-height))`,
  width: '100%',
});

const Wrap = styled.div({
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  background: `url(${chatBackgroundImg}) no-repeat`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
});

const StyledGetStarted = styled(ChatGetStarted)({
  marginTop: 30,
});

const MainChat = styled.div({
  display: 'flex',
  boxSizing: 'border-box',
  width: '100%',
  height: '100%',
  // height: `calc(100% - ${FooterHeight}px)`,
});

const VideoWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
  display: 'grid',
  gridTemplateColumns: expanded ? '1fr 0' : '9fr 11fr',
  flexGrow: 1,
  height: '100%',
  overflow: 'hidden',
}));

const ChatInteractionWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  height: '100%',
  overflow: 'hidden',
});
